import { PARAMETERS } from "../constants";
import { Buff, Data, Hash, SbParameters } from "../types";
import { createBuffer } from "./create-buffer";
import { sha256 } from "./sha256";

function _sum(A1: number[], A2: number[], S: number[]) {
  for (let i = 0; i < 8; ++i) {
    S[i] = A1[i] ^ A2[i];
  }

  return S;
}

function _loop32encode(B: number[], X: number[], CNA: SbParameters["CNA"]) {
  for (let i = 0, j: number, n: number, v: number; ; ) {
    n = B[0] + X[i < 24 ? i & 7 : 31 - i];
    v = 0;
    for (j = 0; j < 8; ++j, n >>>= 4) {
      v |= CNA[j][n & 15] << (j << 2);
    }

    v = ((v << 11) | (v >>> 21)) ^ B[1];
    if (++i < 32) {
      B[1] = B[0];
      B[0] = v;
      continue;
    }

    B[1] = v;

    return B;
  }
}

function _shift64(BUFF: Buff, count: number) {
  for (let B = new Array(2); count--; ) {
    B[0] = BUFF[0] ^ BUFF[2];
    B[1] = BUFF[1] ^ BUFF[3];
    BUFF[0] = BUFF[2];
    BUFF[1] = BUFF[3];
    BUFF[2] = BUFF[4];
    BUFF[3] = BUFF[5];
    BUFF[4] = BUFF[6];
    BUFF[5] = BUFF[7];
    BUFF[6] = B[0];
    BUFF[7] = B[1];
  }

  return BUFF;
}

function _shift16(BUFF: Buff, count: number) {
  for (let v: number; count--; ) {
    v =
      BUFF[0] ^
      (BUFF[0] >>> 16) ^
      BUFF[1] ^
      (BUFF[1] >>> 16) ^
      BUFF[6] ^
      (BUFF[7] >>> 16);
    BUFF[0] = (BUFF[0] >>> 16) | (BUFF[1] << 16);
    BUFF[1] = (BUFF[1] >>> 16) | (BUFF[2] << 16);
    BUFF[2] = (BUFF[2] >>> 16) | (BUFF[3] << 16);
    BUFF[3] = (BUFF[3] >>> 16) | (BUFF[4] << 16);
    BUFF[4] = (BUFF[4] >>> 16) | (BUFF[5] << 16);
    BUFF[5] = (BUFF[5] >>> 16) | (BUFF[6] << 16);
    BUFF[6] = (BUFF[6] >>> 16) | (BUFF[7] << 16);
    BUFF[7] = (BUFF[7] >>> 16) | (v << 16);
  }

  return BUFF;
}

function _step(
  BUFF: Buff,
  HASH: Hash,
  CNA: SbParameters["CNA"],
  CP: SbParameters["CP"]
) {
  const V = [
    BUFF[0],
    BUFF[1],
    BUFF[2],
    BUFF[3],
    BUFF[4],
    BUFF[5],
    BUFF[6],
    BUFF[7],
  ];

  const U = [
    HASH[0],
    HASH[1],
    HASH[2],
    HASH[3],
    HASH[4],
    HASH[5],
    HASH[6],
    HASH[7],
  ];

  const X = new Array(8);
  const E = new Array(8);
  const W = new Array(8);
  const B = new Array(2);

  for (let i = 0, j, l, k; ; ) {
    _sum(U, V, W);
    for (k = 0; k < 8; ++k) {
      X[k] = 0;
      for (j = 0, l = (k & 3) << 3; j < 4; ++j) {
        X[k] |= ((W[(j << 1) + (k >> 2)] >>> l) & 0xff) << (j << 3);
      }
    }

    B[0] = HASH[i];
    B[1] = HASH[i + 1];
    _loop32encode(B, X, CNA);
    E[i++] = B[0];
    E[i++] = B[1];
    if (i === 8) {
      break;
    }

    _shift64(V, 2);
    _shift64(U, 1);
    if (i === 4) {
      _sum(U, CP, U);
    }
  }

  return _shift16(
    _sum(_shift16(_sum(_shift16(E, 12), BUFF, E), 1), HASH, HASH),
    61
  );
}

function _calc(data: Data, CNA: SbParameters["CNA"], CP: SbParameters["CP"]) {
  const ACCM = [0, 0, 0, 0, 0, 0, 0, 0];
  let HASH = [0, 0, 0, 0, 0, 0, 0, 0];
  const SIZE = [0, 0, 0, 0, 0, 0, 0, 0];

  for (let i = 0, size = data.length; size > 0; ) {
    let n = size < 32 ? size << 3 : 256;
    const b = [0, 0, 0, 0, 0, 0, 0, 0];

    for (let j = 0, v = 0; j < 32; ++j) {
      const ii = (j & 3) << 3;
      const iv = j >> 2;

      if (n) {
        n += (SIZE[iv] >>> ii) & 0xff;
        SIZE[iv] &= ~(0xff << ii);
        SIZE[iv] |= (n & 0xff) << ii;
        n >>>= 8;
      }

      if (size) {
        --size;
        const code = data[i++];

        b[iv] |= code << ii;
        v += code;
      } else if (v === 0) {
        continue;
      }

      v += (ACCM[iv] >>> ii) & 0xff;
      ACCM[iv] &= ~(0xff << ii);
      ACCM[iv] |= (v & 0xff) << ii;
      v >>>= 8;
    }

    _step(b, HASH, CNA, CP);
  }

  _step(SIZE, HASH, CNA, CP);
  HASH = _step(ACCM, HASH, CNA, CP);
  const buffer = createBuffer();

  for (let i = 0; i < HASH.length; ++i) {
    buffer.addInt(HASH[i]);
  }

  return buffer.toHexString();
}

function calcHash(data: Data, parameters: SbParameters) {
  return _calc(data, parameters.CNA, parameters.CP);
}

export function calc(data: number[]) {
  return calcHash(data, PARAMETERS.SB);
}

export function calcSha256(data: number[]) {
  return sha256(data);
}
